import type { ButtonHTMLAttributes, FC } from "react";

import classnames from "classnames";

import { ChevronForwardIcon } from "@jti/brand-icons/chevron-forward";
import { ProfileDefaultIcon } from "@jti/brand-icons/profile-default";
import { Icon } from "@jti/core-components/components/Icon";

import styles from "./UserAccountBanner.module.scss";

export const USER_ACCOUNT_BANNER_VARIANTS = ["neutral", "club-tier1", "club-tier2", "club-tier3", "club"] as const;

export type UserAccountBannerVariant = (typeof USER_ACCOUNT_BANNER_VARIANTS)[number];

export type UserAccountBannerProps = {
  userName: string;
  titleLabel?: string;
  variant?: UserAccountBannerVariant;
  greeting?: string;
  userDisplayMode?: "icon" | "initials";
  initials?: string;
  tierLabel?: string;
  pointsBalance?: string | number;
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "children">;

export const UserAccountBanner: FC<UserAccountBannerProps> = ({
  variant = "neutral",
  userName,
  titleLabel,
  greeting = "",
  userDisplayMode = "icon",
  initials,
  tierLabel,
  pointsBalance,
  ...rest
}) => {
  const hasSubtitle = Boolean(tierLabel || pointsBalance);

  return (
    <button type="button" className={classnames(styles.banner, styles[variant])} {...rest}>
      {userDisplayMode === "initials" ? (
        <span className={styles.avatarInitials} aria-hidden="true">
          {initials}
        </span>
      ) : (
        <span className={styles.avatarIcon}>
          <Icon icon={ProfileDefaultIcon} size="sm" />
        </span>
      )}

      <span className={styles.content}>
        <span className={styles.title}>{titleLabel ?? `${greeting} ${userName}`}</span>
        {hasSubtitle && (
          <span className={styles.subtitle}>
            {tierLabel && (
              <span className={styles.tier}>
                <span>{tierLabel}</span>
                <span aria-hidden="true">·</span>
              </span>
            )}
            {pointsBalance && <span>{pointsBalance}</span>}
          </span>
        )}
      </span>

      <Icon className={styles.chevron} icon={ChevronForwardIcon} size="sm" />
    </button>
  );
};
